class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindCloseButton();
  }

  bindCloseButton() {
    const closeBtn = this.querySelector('.drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) {
        this.close();
      } else {
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        if (this.classList.contains('active')) {
          this.close();
        } else {
          this.open(cartLink);
        }
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          document.getElementById('CartDrawer') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  refreshDrawer() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newRoot = doc.querySelector('cart-drawer');
        if (newRoot) {
          this.classList.toggle('is-empty', newRoot.classList.contains('is-empty'));
        }

        const newItems = doc.querySelector('cart-drawer-items');
        const currentItems = this.querySelector('cart-drawer-items');
        if (newItems && currentItems) currentItems.replaceWith(newItems);

        const newFooter = doc.getElementById('CartDrawer-Footer');
        const currentFooter = document.getElementById('CartDrawer-Footer');
        if (newFooter && currentFooter) currentFooter.replaceWith(newFooter);

        // cart-icon-bubble
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
          .then((r) => r.text())
          .then((bubbleHtml) => {
            const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const newBubble = bubbleDoc.querySelector('.shopify-section');
            const currentBubble = document.getElementById('cart-icon-bubble');
            if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
          })
          .catch((e) => console.error(e));
      })
      .catch((e) => console.error(e));
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.refreshDrawer().finally(() => {
      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.bindCloseButton();
        this.open();
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
        innerSelector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((r) => r.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = this.querySelector(`#Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        // is-empty
        drawer.classList.toggle('is-empty', parsedState.item_count === 0);

        // cart-drawer-items depuis la section
        const drawerHtml = parsedState.sections['cart-drawer'];
        if (drawerHtml) {
          const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');

          const newItems = doc.querySelector('cart-drawer-items');
          const currentItems = drawer.querySelector('cart-drawer-items');
          if (newItems && currentItems) currentItems.replaceWith(newItems);

          const newFooter = doc.getElementById('CartDrawer-Footer');
          const currentFooter = document.getElementById('CartDrawer-Footer');
          if (newFooter && currentFooter) currentFooter.replaceWith(newFooter);
        }

        // cart-icon-bubble
        const bubbleHtml = parsedState.sections['cart-icon-bubble'];
        if (bubbleHtml) {
          const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const newBubble = bubbleDoc.querySelector('.shopify-section');
          const currentBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
        }

        this.updateLiveRegions(line, '');
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);